// ── Invite accept screen: pure logic (testable, no DOM/api deps) ──
import type { WorkspaceInvitationAcceptanceMode, WorkspaceInvitationInspection } from './types';

export const INVITE_ACCEPT_HASH_PREFIX = '#accept-invite';

// The invitation mail links to `#accept-invite?token=…`. The token lives in the
// URL fragment so it never reaches the server log; this reads it back out.
export function classifyInviteAcceptHash(hash: string): string | null {
  if (!hash || !hash.startsWith(INVITE_ACCEPT_HASH_PREFIX)) return null;
  const rest = hash.slice(INVITE_ACCEPT_HASH_PREFIX.length);
  if (rest && !rest.startsWith('?')) return null;
  const token = new URLSearchParams(rest.replace(/^\?/, '')).get('token');
  const trimmed = token?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
}

export function buildInviteAcceptHash(token: string): string {
  return `${INVITE_ACCEPT_HASH_PREFIX}?token=${encodeURIComponent(token)}`;
}

// When the invitee's own workspace holds nothing, there is nothing to delete or
// migrate: accept with delete_existing and skip the choice.
export function resolveAcceptanceMode(
  inspection: Pick<WorkspaceInvitationInspection, 'requires_mode_choice'>,
  selected: WorkspaceInvitationAcceptanceMode | null,
): WorkspaceInvitationAcceptanceMode | null {
  if (!inspection.requires_mode_choice) return 'delete_existing';
  return selected;
}

const INVITE_ACCEPT_ERROR_MESSAGE_KEY: Record<string, string> = {
  invitation_not_found: 'inviteAcceptErrorNotFound',
  invitation_revoked: 'workspaceErrorInvitationRevoked',
  invitation_consumed: 'workspaceErrorInvitationConsumed',
  invitation_expired: 'workspaceErrorInvitationExpired',
  invitation_email_mismatch: 'workspaceErrorInvitationEmailMismatch',
  invitation_conflict: 'inviteAcceptErrorAlreadyMember',
  active_membership_conflict: 'workspaceErrorActiveMembershipConflict',
  multiple_memberships_detected: 'workspaceErrorMultipleMemberships',
  migration_conflict: 'workspaceErrorMigrationConflict',
  invalid_acceptance_mode: 'workspaceErrorInvalidAcceptanceMode',
  invalid_invite_token: 'inviteAcceptErrorNotFound',
};

// Maps an accept / inspect error `code` to the i18n key of a human reason.
// Unknown codes fall back to the generic workspace message.
export function inviteAcceptErrorMessageKey(code: string | null | undefined): string {
  if (!code) return 'workspaceErrorGeneric';
  return INVITE_ACCEPT_ERROR_MESSAGE_KEY[code] ?? 'workspaceErrorGeneric';
}
